import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

const STICKY_VISIBLE_CLASS = 'foxnaro-sticky-purchase-visible';
const FORMS_EMBED_SELECTOR = 'shopify-forms-embed';
const FORMS_VISIBLE_ATTRIBUTE = 'data-foxnaro-sticky-purchase-visible';
const FORMS_STYLE_ATTRIBUTE = 'data-foxnaro-sticky-purchase-offset-style';
const FORMS_TEASER_ACCENT_ATTRIBUTE = 'data-foxnaro-teaser-accent';

/**
 * Adds a semantic span around the promotional part of the exact teaser copy so
 * it can use the Foxnaro accent color without rebuilding the Forms control.
 * If Shopify changes the copy or markup, the native teaser is left untouched.
 *
 * @param {ShadowRoot} shadowRoot
 */
function brandShopifyFormsTeaser(shadowRoot) {
  const teaser = shadowRoot.querySelector(
    "[data-form-root='true'][data-current-step='tease'][data-position='bottom-left'] [data-testid='form-teaser']"
  );
  if (!(teaser instanceof HTMLButtonElement)) return;

  const heading = teaser.querySelector('h3');
  if (!(heading instanceof HTMLHeadingElement)) return;
  if (heading.querySelector(`[${FORMS_TEASER_ACCENT_ATTRIBUTE}]`)) return;
  if (heading.textContent?.replace(/\s+/g, ' ').trim() !== 'Get 10% Off') return;

  const accent = document.createElement('span');
  accent.setAttribute(FORMS_TEASER_ACCENT_ATTRIBUTE, '');
  accent.textContent = '10% Off';
  heading.replaceChildren(document.createTextNode('Get '), accent);
}

/**
 * Keeps Shopify Forms' bottom teaser above the sticky purchase bar. Shopify
 * Forms renders inside an open shadow root, so the offset rule must live in
 * that shadow root. Stable custom-element and data attributes are used instead
 * of the app's generated IDs and class names.
 *
 * @param {boolean} visible
 */
function syncShopifyFormsTeaserOffset(visible) {
  const formsEmbeds = document.querySelectorAll(FORMS_EMBED_SELECTOR);

  for (const formsEmbed of formsEmbeds) {
    if (!(formsEmbed instanceof HTMLElement)) continue;

    formsEmbed.toggleAttribute(FORMS_VISIBLE_ATTRIBUTE, visible);
    const shadowRoot = formsEmbed.shadowRoot;
    if (!shadowRoot) continue;

    if (!shadowRoot.querySelector(`[${FORMS_STYLE_ATTRIBUTE}]`)) {
      const style = document.createElement('style');
      style.setAttribute(FORMS_STYLE_ATTRIBUTE, '');
      style.textContent = `
        :host([${FORMS_VISIBLE_ATTRIBUTE}])
          [data-form-root='true'][data-current-step='tease'][data-layout='overlay'][data-position^='bottom-'] {
          bottom: max(
            0px,
            calc(
              var(--foxnaro-sticky-purchase-height, 80px) + 14px - var(--app-embed-padding-y, 0px)
            )
          ) !important;
        }

        [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
          [data-testid='form-teaser'] {
          background-color: #ffffff !important;
          border: 0 !important;
          box-shadow:
            inset 0 0 0 1px rgba(188, 91, 34, 0.24),
            0 2px 8px rgba(48, 35, 27, 0.1),
            0 18px 48px rgba(48, 35, 27, 0.2) !important;
          transition:
            background-color 180ms ease,
            box-shadow 180ms ease !important;
        }

        [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
          [data-testid='form-teaser'] h3 {
          color: #171412 !important;
          font-weight: 700 !important;
        }

        [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
          [data-testid='form-teaser'] [${FORMS_TEASER_ACCENT_ATTRIBUTE}] {
          color: #bc5b22 !important;
        }

        [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
          [data-testid='form-teaser'] [role='button'][aria-label='Close modal'] {
          color: #3f3b38 !important;
        }

        [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
          [data-testid='form-teaser'] [role='button'][aria-label='Close modal'] svg,
        [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
          [data-testid='form-teaser'] [role='button'][aria-label='Close modal'] path {
          fill: currentColor !important;
        }

        @media (hover: hover) and (pointer: fine) {
          [data-form-root='true'][data-current-step='tease'][data-position='bottom-left']
            [data-testid='form-teaser']:hover {
            background-color: #fff9f5 !important;
            box-shadow:
              inset 0 0 0 1px rgba(188, 91, 34, 0.34),
              0 3px 10px rgba(48, 35, 27, 0.12),
              0 20px 52px rgba(48, 35, 27, 0.22) !important;
          }
        }
      `;
      shadowRoot.append(style);
    }

    brandShopifyFormsTeaser(shadowRoot);
  }
}

window.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const changeOptionsButton = target.closest(
      'foxnaro-sticky-purchase .foxnaro-sticky-purchase__change-options'
    );
    if (changeOptionsButton instanceof HTMLAnchorElement) {
      const component = changeOptionsButton.closest('foxnaro-sticky-purchase');
      const section = component?.closest('.shopify-section');
      const variantPicker = section?.querySelector('variant-picker');
      if (!(variantPicker instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      return;
    }

    const stickyButton = target.closest('foxnaro-sticky-purchase .foxnaro-sticky-purchase__button');
    if (!(stickyButton instanceof HTMLButtonElement) || stickyButton.disabled) return;

    const component = stickyButton.closest('foxnaro-sticky-purchase');
    const section = component?.closest('.shopify-section');
    const productForm = section?.querySelector(
      `product-form-component[data-product-id="${component?.dataset.productId}"]`
    );
    const form = productForm?.querySelector('form[action*="/cart/add"]');
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    stickyButton.setAttribute('aria-busy', 'true');
    const nativeAddToCartButton = form.querySelector('[ref="addToCartButton"]');
    form.requestSubmit(nativeAddToCartButton instanceof HTMLButtonElement ? nativeAddToCartButton : undefined);
  },
  { capture: true }
);

/**
 * Initializes the homepage sticky purchase bar after the complete section DOM
 * is available. Variant resolution and cart behavior remain delegated to
 * Horizon's existing picker and product form.
 *
 * @param {ParentNode} root
 */
function initStickyPurchaseBars(root = document) {
  const components = root.querySelectorAll('foxnaro-sticky-purchase');

  for (const component of components) {
    if (!(component instanceof HTMLElement) || component.dataset.initialized === 'true') continue;

    const section = component.closest('.shopify-section');
    const bar = component.querySelector('[ref="stickyBar"]');
    if (!section || !(bar instanceof HTMLElement)) continue;

    const getProductForm = () =>
      section.querySelector(`product-form-component[data-product-id="${component.dataset.productId}"]`);
    const getBuyButtonsBlock = () => getProductForm()?.closest('.buy-buttons-block');
    const getStickyButton = () => component.querySelector('[ref="addToCartButton"]');
    const syncVariantPickerTarget = () => {
      const variantPicker = section.querySelector('variant-picker');
      if (variantPicker instanceof HTMLElement) variantPicker.id = 'foxnaro-featured-product-options';
    };
    const syncStickyFormTarget = () => {
      const stickyButton = getStickyButton();
      const productForm = getProductForm()?.querySelector('form[action*="/cart/add"]');
      if (!(stickyButton instanceof HTMLButtonElement) || !(productForm instanceof HTMLFormElement)) return;
      const productFormId = productForm.getAttribute('id');
      if (productFormId) stickyButton.setAttribute('form', productFormId);
    };

    let buyButtonsBlock = getBuyButtonsBlock();
    if (!(buyButtonsBlock instanceof HTMLElement)) continue;

    component.dataset.initialized = 'true';
    const abortController = new AbortController();
    const { signal } = abortController;

    const newsletterFooterBoundary =
      document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    let newsletterOrFooterVisible = false;

    const syncStickyPageState = (visible) => {
      const stickyHeight = Math.ceil(bar.getBoundingClientRect().height);
      if (stickyHeight > 0) {
        document.documentElement.style.setProperty('--foxnaro-sticky-purchase-height', `${stickyHeight}px`);
      }

      document.body?.classList.toggle(STICKY_VISIBLE_CLASS, visible);
      syncShopifyFormsTeaserOffset(visible);
    };

    const show = () => {
      bar.dataset.stuck = 'true';
      syncStickyPageState(true);
    };
    const hide = () => {
      bar.dataset.stuck = 'false';
      syncStickyPageState(false);
    };

    const boundaryIsVisible = () => {
      if (!(newsletterFooterBoundary instanceof HTMLElement)) return false;

      const boundaryRect = newsletterFooterBoundary.getBoundingClientRect();
      return boundaryRect.top <= window.innerHeight * 0.75 && boundaryRect.bottom > 0;
    };

    const updateVisibility = () => {
      const currentBuyButtonsBlock = getBuyButtonsBlock();
      if (!(currentBuyButtonsBlock instanceof HTMLElement)) {
        hide();
        return;
      }

      if (currentBuyButtonsBlock !== buyButtonsBlock) {
        buyButtonsObserver.disconnect();
        buyButtonsObserver.observe(currentBuyButtonsBlock);
        buyButtonsBlock = currentBuyButtonsBlock;
      }

      newsletterOrFooterVisible = boundaryIsVisible();
      const purchaseAreaHasPassed = buyButtonsBlock.getBoundingClientRect().bottom < 0;

      if (purchaseAreaHasPassed && !newsletterOrFooterVisible) {
        show();
      } else {
        hide();
      }
    };

    const buyButtonsObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      updateVisibility();
    });
    buyButtonsObserver.observe(buyButtonsBlock);

    if (newsletterFooterBoundary) {
      const newsletterFooterObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          newsletterOrFooterVisible = entry.isIntersecting;
          updateVisibility();
        },
        { rootMargin: '0px 0px -25% 0px' }
      );
      newsletterFooterObserver.observe(newsletterFooterBoundary);
    }

    let lastVisibilityUpdate = 0;
    const scheduleVisibilityUpdate = () => {
      const now = Date.now();
      if (now - lastVisibilityUpdate < 100) return;
      lastVisibilityUpdate = now;
      syncVariantPickerTarget();
      syncStickyFormTarget();
      updateVisibility();
    };

    const scrollContainer = document.querySelector('.page-wrapper');
    const scrollTarget = scrollContainer instanceof HTMLElement ? scrollContainer : window;

    scrollTarget.addEventListener('scroll', scheduleVisibilityUpdate, { passive: true });
    window.addEventListener('resize', scheduleVisibilityUpdate);
    scheduleVisibilityUpdate();

    window.setInterval(() => {
      if (!component.isConnected) return;

      syncVariantPickerTarget();
      syncStickyFormTarget();
      updateVisibility();
    }, 250);

    section.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

        event.promise
          .then(({ detail }) => {
            if (!detail?.html) return;

            const { html, productId, resource: variant } = detail;
            if (productId && productId !== component.dataset.productId) return;

            const newComponent = html.querySelector('foxnaro-sticky-purchase');
            const newBar = newComponent?.querySelector('[ref="stickyBar"]');
            if (!(newComponent instanceof HTMLElement) || !(newBar instanceof HTMLElement)) return;

            const stuck = bar.dataset.stuck || 'false';
            morph(bar, newBar, { childrenOnly: true });
            bar.dataset.stuck = stuck;
            component.dataset.variantAvailable = newComponent.dataset.variantAvailable || 'false';
            component.dataset.currentVariantId = variant?.id ? String(variant.id) : '';
            syncVariantPickerTarget();
            syncStickyFormTarget();
          })
          .catch((error) => {
            if (error?.name !== 'AbortError') {
              console.warn('[foxnaro-sticky-purchase] Variant update failed:', error);
            }
          });
      },
      { signal }
    );

    const resetCartState = (event) => {
      const reset = () => {
        getStickyButton()?.removeAttribute('aria-busy');
        const targetButton = getProductForm()?.querySelector('[ref="addToCartButton"]');
        if (targetButton instanceof HTMLElement) targetButton.dataset.puppet = 'false';
      };

      if ('promise' in event && event.promise instanceof Promise) {
        event.promise.finally(reset);
      } else {
        reset();
      }
    };

    document.addEventListener(StandardEvents.cartLinesUpdate, resetCartState, { signal });
    document.addEventListener(StandardEvents.cartError, resetCartState, { signal });
    syncVariantPickerTarget();
    syncStickyFormTarget();
    component.dataset.controllerReady = 'true';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initStickyPurchaseBars(), { once: true });
} else {
  initStickyPurchaseBars();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initStickyPurchaseBars(event.target);
});
